import type { DoorLook, DoorStyle } from '../drawing/door';
import type { PieceKind } from '../doors/drilling';
import { ApiError, http, SPATIAL_ROOT, type Fetch } from './http';

/**
 * The door catalogue the storefront builders read: collections, ranges and
 * colours. Shoppers only ever see OUR names; which manufacturer makes a range
 * is internal and is not part of this shape at all.
 *
 * <p>Read live from the shop's published door ranges
 * (`GET /api/storefront/extensions/spatial/kitchen/door-ranges`), which list
 * every range and colour the shop actually stocks pieces in. The standard sizes
 * and their SKUs stay on the server: a made-to-measure piece is priced and
 * added to the basket by the server from the spec (DOOR_AND_CARCASS_BUILDERS_PLAN.md
 * F1–F3), so the browser never needs them and never supplies a price.
 */

/**
 * How a collection's supplier prices a made-to-measure piece. The mode only
 * decides the COST; the selling price comes from the brand's markup rule on the
 * server, never from the browser.
 * <ul>
 *   <li>`band`: the next standard size up in the supplier's size table.</li>
 *   <li>`area`: per m² of the exact size, with a minimum charge.</li>
 * </ul>
 */
export type PricingMode = 'band' | 'area';

export interface DoorCollection {
  id: string;
  name: string;
  note: string;
  pricing: PricingMode;
}

export interface DoorColour {
  id: string;
  name: string;
  hex: string;
  finish: string;
  wood?: boolean;
}

export type DoorFamily = 'shaker' | 'handleless' | 'slab' | 'panelled' | 'inframe' | 'glazed';

/** The smallest and largest standard piece of one kind in a range, mm. */
export interface SizeRange {
  minH: number;
  maxH: number;
  minW: number;
  maxW: number;
}

export interface DoorRange {
  id: string;
  name: string;
  collection: string;
  style: DoorStyle;
  family: DoorFamily;
  finish: string;
  build: string;
  colours: string[];
  /** The kinds of piece the range is sold in, with the sizes it covers. */
  sizes: Partial<Record<PieceKind, SizeRange>>;
  /** Can be painted to order in any colour the shopper names. */
  ral?: boolean;
  /** Made for a handle rail: no handle drilling offered. */
  noHandle?: boolean;
  /** Made tall enough for wardrobes. */
  wardrobe?: boolean;
  badge?: string;
  /** How the range's door is drawn, where the shop publishes it. */
  look?: DoorLook;
}

/** The range's look for drawing, with the shine its finish gives it. */
export function rangeLook(range: Pick<DoorRange, 'look' | 'finish'>): DoorLook {
  const finish = range.finish.toLowerCase();
  const sheen: DoorLook['sheen'] = finish.includes('gloss') ? 'gloss' : finish.includes('matt') ? 'matt' : 'satin';
  return { ...range.look, sheen };
}

export interface DoorCatalogue {
  collections(): DoorCollection[];
  ranges(): DoorRange[];
  colours(): DoorColour[];
}

/** Loads the catalogue; resolves to null when the shop has published no door ranges. */
export interface DoorCatalogueSource {
  load(): Promise<DoorCatalogue | null>;
  /**
   * The catalogue if it is already in hand, so a block can render it on its
   * first pass (and on the server) instead of flashing a loading state.
   * `undefined` means "not loaded yet"; `null` means "loaded: no doors".
   */
  peek?(): DoorCatalogue | null | undefined;
}

export const FAMILIES: ReadonlyArray<{ id: DoorFamily | 'all' | 'wardrobe'; name: string }> = [
  { id: 'all', name: 'All styles' },
  { id: 'shaker', name: 'Shaker' },
  { id: 'handleless', name: 'Handleless' },
  { id: 'slab', name: 'Slab' },
  { id: 'panelled', name: 'Panelled & fluted' },
  { id: 'inframe', name: 'In-frame look' },
  { id: 'glazed', name: 'Glazed' },
  { id: 'wardrobe', name: 'Wardrobe' },
];

export function staticDoorCatalogue(data: {
  collections: DoorCollection[];
  ranges: DoorRange[];
  colours: DoorColour[];
}): DoorCatalogue {
  return { collections: () => data.collections, ranges: () => data.ranges, colours: () => data.colours };
}

/** A range as the server publishes it: the kinds it is sold in, each with its size span. */
type PublishedRange = Omit<DoorRange, 'sizes'> & { kinds: Array<SizeRange & { kind: PieceKind }> };

interface DoorRangesIndex {
  collections: DoorCollection[];
  colours: DoorColour[];
  ranges: PublishedRange[];
}

export function fromPublished(index: DoorRangesIndex): DoorCatalogue {
  return staticDoorCatalogue({
    collections: index.collections,
    colours: index.colours,
    ranges: index.ranges.map(({ kinds, ...range }) => ({
      ...range,
      sizes: Object.fromEntries(kinds.map(({ kind, minH, maxH, minW, maxW }) => [kind, { minH, maxH, minW, maxW }])),
    })),
  });
}

export function liveDoorCatalogue(fetchImpl?: Fetch): DoorCatalogueSource {
  const api = http(fetchImpl);
  let loaded: Promise<DoorCatalogue | null> | null = null;
  let held: DoorCatalogue | null | undefined;
  return {
    peek: () => held,
    load() {
      if (!loaded) {
        loaded = api
          .get<DoorRangesIndex>(`${SPATIAL_ROOT}/kitchen/door-ranges`)
          .then((index) => (index.ranges.length ? fromPublished(index) : null))
          .then((catalogue) => (held = catalogue))
          .catch((error: unknown) => {
            if (error instanceof ApiError && error.status === 404) return (held = null);
            // A failed read is not "no doors": let the next call try again.
            loaded = null;
            throw error;
          });
      }
      return loaded;
    },
  };
}
